/**
 * Rounded-rectangle SDF instanced pipeline.
 *
 * Pass 1 (this): one instanced draw of N unit-quads → offscreen FBO.
 * The vertex shader positions each quad in clip space using the per-instance
 * rect/transform. The fragment shader evaluates an analytic SDF in local
 * coordinates and composites fill+stroke+shadow.
 *
 * Shader notes (per agent-memory pitfalls):
 *  - Branchless SDF math (no if/else inside vec3-returning mixes).
 *  - Single vec3(0.10,0.10,0.10) ctors — never vec3(0.10).
 *  - Shadow is a smoothed distance to an expanded+offset rect (cheap, stable,
 *    no multi-sample Gaussian — per the brief's "cheap blur falloff").
 *  - Premultiplied alpha throughout, matches glContext blend state.
 *  - Velocity is current-clip-center minus prev-clip-center, written into
 *    a varying. Sprint 3's postprocess reads it for motion blur. Sprint 1's
 *    postprocess is a no-op so the varying is unused but the math is correct.
 *
 * Layer 3 extensions:
 *  - Per-corner radii: vec4 attribute aRadii (TL, TR, BR, BL).
 *  - Filter (blur/brightness/contrast/saturate/hue-rotate/invert): applied
 *    to the final color in the FS. Cheap variants — blur uses a tiny fwidth-
 *    based soften, not Gaussian.
 *  - Mix-blend-mode is approximated via separate blending for the four most
 *    common modes (multiply, screen, darken, lighten) using the framebuffer's
 *    existing color. For PoC we read the framebuffer via the post-process
 *    pass — see postProcess.ts. Here we just output the source color and let
 *    postprocess blend.
 *
 * Layer 5 extension:
 *  - backdrop-filter: blur(12px). The fragment shader samples the scene FBO
 *    texture at the fragment's screen-space UV, applies a cheap 9-tap radial
 *    blur weighted by uBackdropBlur, and composites the blurred scene
 *    BENEATH the node's own fill+stroke. Shadow stays cheap and is composited
 *    last (matches the existing chain). Gated by uBackdropBlur > 0 so the
 *    common case (no glass) pays zero cost.
 */

import { ATTR_LOCATIONS, BYTES_PER_INSTANCE, FLOATS_PER_INSTANCE, type CompiledScene } from "../compiler";
import { linkProgram, RECT_ATTRIBS } from "./glContext";

const VERT_SRC = `#version 300 es
precision highp float;

// per-vertex (unit quad) — derived from gl_VertexID. The quad is two
// triangles drawn as TRIANGLES, so vertex IDs 0..5 map to:
//   0:(0,0) 1:(1,0) 2:(0,1)   3:(0,1) 4:(1,0) 5:(1,1)
// No per-vertex attribute needed — this frees location 13 for the
// per-instance aSdfKind, leaving 14/15 for aSdfParamsA/B.

// per-instance (matches compiler.ts layout — 16 vec4 attributes total)
layout(location = 0) in vec4 aPosSize;     // x,y,w,h  (CSS px)
layout(location = 1) in vec4 aFill;
layout(location = 2) in vec4 aStroke;      // width,r,g,b
layout(location = 3) in vec4 aShadow;      // offX,offY,blur,spread
layout(location = 4) in vec4 aShadowColor;
layout(location = 5) in vec4 aXform;       // cos,sin,scaleX,scaleY
layout(location = 6) in vec4 aPrev;        // prevX,prevY,prevCos,prevSin
layout(location = 7) in vec4 aMisc;        // radius,opacity,z,strokeA
layout(location = 8) in vec4 aHalfSize;    // halfW,halfH,_,_
layout(location = 9) in vec4 aRadii;       // TL,TR,BR,BL radii
layout(location = 10) in vec4 aFilterA;    // blur,brightness,contrast,saturate
layout(location = 11) in vec4 aFilterB;    // hueRotate,invert,_,_
layout(location = 12) in vec4 aGradient;   // endR,endG,endB,angle (angle=0 means no gradient)

// SDF library — see SDF_KIND_IDS in compiler.ts. Kind id is packed in
// aMisc.w as id/15.0; vertex shader decodes it to vSdfKind. Per-instance
// sdfParamsA/sdfParamsB carry the shape-specific numeric parameters.
layout(location = 13) in vec4 aSdfParamsA; // p0,p1,p2,p3 — shape-specific
layout(location = 14) in vec4 aSdfParamsB; // p4,p5,p6,p7 — shape-specific

uniform vec2 uCanvasSize;   // backbuffer size in CSS px
uniform float uDevicePixelRatio;

out vec2 vLocal;            // local-space coord centered at rect center, unrotated, unscaled
out vec2 vLocalScaled;      // local-space after scaleX/scaleY, used for SDF corners
out vec2 vCorner;
out vec4 vFill;
out vec4 vStroke;
out vec4 vShadow;
out vec4 vShadowColor;
out float vOpacity;
out float vStrokeWidth;
out vec2 vVelClip;          // velocity in clip-space units per frame
out vec2 vScreenUV;         // [0,1] UV into the scene FBO texture (Layer 5 backdrop sample)
flat out vec2 vHalfSize;    // halfW,halfH (flat — no interp)
flat out vec4 vRadii;       // per-corner radii (flat — no interp)
flat out vec4 vFilterA;     // blur,brightness,contrast,saturate (flat — no interp)
flat out vec4 vFilterB;     // hueRotate,invert,_,_ (flat — no interp)
flat out vec4 vGradient;    // endR,endG,endB,angle (flat — no interp)
flat out float vSdfKind;    // SDF shape id (flat — no interp). Decoded
                              //   from aMisc.w by multiplying by 15.
flat out vec4 vSdfParamsA;  // p0..p3 (flat — no interp)
flat out vec4 vSdfParamsB;  // p4..p7 (flat — no interp)

vec2 toClip(vec2 px) {
  // CSS px → clip space [-1,1]
  return vec2(
    (px.x / uCanvasSize.x) * 2.0 - 1.0,
    1.0 - (px.y / uCanvasSize.y) * 2.0
  );
}

void main() {
  // Unit-quad corner derived from gl_VertexID. Vertex layout in QUAD_VERTS
  // (TRIANGLES, 6 verts):
  //   id 0:(0,0)  1:(1,0)  2:(0,1)   3:(0,1)  4:(1,0)  5:(1,1)
  vec2 aCorner = vec2(
    (gl_VertexID == 1 || gl_VertexID == 4 || gl_VertexID == 5) ? 1.0 : 0.0,
    (gl_VertexID == 2 || gl_VertexID == 3 || gl_VertexID == 5) ? 1.0 : 0.0
  );

  // Current center in CSS px
  float cx = aPosSize.x + aPosSize.z * 0.5;
  float cy = aPosSize.y + aPosSize.w * 0.5;

  // Local untransformed corner (in [0,1] across rect; treat as -halfSize..+halfSize)
  vec2 local = (aCorner - 0.5) * aPosSize.zw;
  vec2 scaled = vec2(local.x * aXform.z, local.y * aXform.w);
  float c = aXform.x, s = aXform.y;
  vec2 rotated = vec2(c * scaled.x - s * scaled.y, s * scaled.x + c * scaled.y);
  vec2 world = vec2(cx, cy) + rotated;
  vec2 clip = toClip(world);

  // Velocity from previous-center vs current-center, in clip space
  float pcx = aPrev.x + aPosSize.z * 0.5;
  float pcy = aPrev.y + aPosSize.w * 0.5;
  vec2 pclip = toClip(vec2(pcx, pcy));
  vVelClip = clip - pclip;

  vLocal = local;
  vLocalScaled = scaled;
  vCorner = aCorner;
  vFill = aFill;
  vStroke = vec4(aStroke.yzw, aMisc.w);
  vShadow = aShadow;
  vShadowColor = aShadowColor;
  vOpacity = aMisc.y;
  vStrokeWidth = aStroke.x;
  vHalfSize = aHalfSize.xy;
  vRadii = aRadii;
  vFilterA = aFilterA;
  vFilterB = aFilterB;
  vGradient = aGradient;
  vSdfKind = aMisc.w * 15.0;
  vSdfParamsA = aSdfParamsA;
  vSdfParamsB = aSdfParamsB;
  vScreenUV = clip * 0.5 + 0.5;

  gl_Position = vec4(clip, 0.0, 1.0);
}
`;

const FRAG_SRC = `#version 300 es
precision highp float;
precision highp int;

// =============================================================================
// SDF library — Inigo Quilez 2D distance functions, the same set covered by
// the rustadventure SDF tutorial. Each takes a point p in the rect's local
// space (origin = rect center, units = CSS px) and returns a signed distance
// (negative = inside, positive = outside). The dispatcher below picks the
// right one based on the per-instance vSdfKind id.
// =============================================================================

// sdPerCornerRect is defined further down (after the SDF library proper)
// but called from sdDispatch. Forward-declare so GLSL can resolve it.
float sdPerCornerRect(vec2 p, vec2 b, vec4 r);
// sdStar is defined further down but called from sdPentagram. Forward-declare.
float sdStar(vec2 p, float r, float rf);
// sdHeart is defined further down but called from sdHeartBroken. Forward-declare.
float sdHeart(vec2 p, float r);

float ndot(vec2 a, vec2 b) { return a.x * b.x - a.y * b.y; }

float sdCircle(vec2 p, float r) { return length(p) - r; }

float sdBox(vec2 p, vec2 b) {
  vec2 d = abs(p) - b;
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
}

float sdRoundedBox(vec2 p, vec2 b, float r) {
  r = min(r, min(b.x, b.y));
  vec2 d = abs(p) - (b - r);
  return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - r;
}

// IQ: equilateral triangle inscribed in a circle of radius r.
// NSPP Y-down native: apex at (0, -r) (top of tile), base at +Y
// (bottom). Built as a 3-vertex polygon using the same SDF as sdPolygon.
float sdEquilateralTriangle(vec2 p, float r) {
  // 3 vertices in NSPP Y-down, traced clockwise (visually).
  // i=0: apex (0, -r), i=1: bottom-right (r*sqrt(3)/2, r/2), i=2: bottom-left (-r*sqrt(3)/2, r/2)
  const float k = sqrt(3.0);
  vec2 verts[3];
  verts[0] = vec2(0.0, -r);
  verts[1] = vec2(r * k * 0.5, r * 0.5);
  verts[2] = vec2(-r * k * 0.5, r * 0.5);

  // Standard polygon SDF with even-odd rule.
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 3; i++) {
    int j = (i + 2) % 3;
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    // Even-odd rule (horizontal ray).
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xIntersect = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xIntersect > p.x) {
        insideCount++;
      }
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// IQ: isosceles triangle with given base width w and height h, apex up.
float sdIsoscelesTriangle(vec2 p, float w, float h) {
  p.x = abs(p.x);
  // Two edges: from (-w/2, -h/2) up to (0, h/2) and from (w/2, -h/2) up to (0, h/2).
  vec2 a = vec2(w * 0.5, h * 0.5);
  vec2 b = vec2(0.0, h * 0.5);
  // Line from (-a.x, -a.y) to (b.x, b.y) -- distance to it.
  // Left edge: from (-w/2, -h/2) to (0, h/2)
  vec2 ap = vec2(-p.x - a.x, -p.y - a.y);
  vec2 ab = vec2(b.x - a.x, b.y - a.y);
  // Right edge: from (w/2, -h/2) to (0, h/2) — same shape reflected
  float t = clamp(dot(ap, ab) / max(1e-6, dot(ab, ab)), 0.0, 1.0);
  vec2 bp = ap - ab * t;
  float dLeft = length(bp);
  // Bottom edge distance
  float dBot = p.y + h * 0.5;
  return max(dLeft, dBot);
}

// IQ: parallelogram defined by two edge vectors a and b emanating from origin.
float sdParallelogram(vec2 p, vec2 a, vec2 b) {
  // Use exact SDF for parallelogram (a stretched box via projection).
  vec2 ab = b - a;
  vec2 ap = p - a;
  // Project onto edges; clamp to parallelogram.
  float t1 = clamp(dot(ap, ab) / max(1e-6, dot(ab, ab)), 0.0, 1.0);
  vec2 q1 = ap - ab * t1;
  // Also project onto the other edge (origin to a).
  vec2 oa = a;
  vec2 op = p;
  float t2 = clamp(dot(op, oa) / max(1e-6, dot(oa, oa)), 0.0, 1.0);
  vec2 q2 = op - oa * t2;
  float d = min(length(q1), length(q2));
  // Sign test. GLSL has no vec2 cross; use the 2D pseudo-cross (a.x*b.y - a.y*b.x).
  float c1 = (a.x * b.y - a.y * b.x);
  float cross1 = a.x * p.y - a.y * p.x;
  float cross2 = ab.x * (p.y - b.y) - ab.y * (p.x - b.x);
  // Inside if p is on the correct side of both edges.
  float s = (c1 * cross1 >= 0.0 && c1 * cross2 >= 0.0) ? -1.0 : 1.0;
  return d * s;
}

// IQ: equilateral triangle of given side length s (or use sdEquilateralTriangle for the inscribed form).
float sdTriangleIQ(vec2 p, vec2 p0, vec2 p1, vec2 p2) {
  vec2 e0 = p1 - p0, e1 = p2 - p1, e2 = p0 - p2;
  vec2 v0 = p - p0, v1 = p - p1, v2 = p - p2;
  vec2 pq0 = v0 - e0 * clamp(dot(v0, e0) / max(1e-6, dot(e0, e0)), 0.0, 1.0);
  vec2 pq1 = v1 - e1 * clamp(dot(v1, e1) / max(1e-6, dot(e1, e1)), 0.0, 1.0);
  vec2 pq2 = v2 - e2 * clamp(dot(v2, e2) / max(1e-6, dot(e2, e2)), 0.0, 1.0);
  float s = sign(e0.x * e2.y - e0.y * e2.x);
  vec2 d = min(min(vec2(dot(pq0, pq0), s * (v0.x * e0.y - v0.y * e0.x)),
                vec2(dot(pq1, pq1), s * (v1.x * e1.y - v1.y * e1.x))),
                vec2(dot(pq2, pq2), s * (v2.x * e2.y - v2.y * e2.x)));
  return -sqrt(d.x) * sign(d.y);
}

// IQ: vesica piscis (intersection of two circles offset along x by h).
float sdVesica(vec2 p, float r, float h) {
  // Two circles of radius r centered at (-h/2, 0) and (+h/2, 0), intersect.
  // The vesica is the intersection; the boundary is the lens.
  // The vesica has width 2*sqrt(r^2 - h^2/4) and height r + h/2.
  // For inside: both circle SDFs are <= 0.
  vec2 c1 = vec2(-h * 0.5, 0.0);
  vec2 c2 = vec2( h * 0.5, 0.0);
  float d1 = length(p - c1) - r;
  float d2 = length(p - c2) - r;
  return max(d1, d2);
}

// IQ: oriented vesica — vesica with rotation.
float sdOrientedVesica(vec2 p, float r, float h, float rot) {
  float c = cos(-rot), s = sin(-rot);
  p = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
  return sdVesica(p, r, h);
}

// IQ: vesica-like shape but with rounded corners (the "moon" / crescent).
// Crescent: ring intersected with a half-plane offset from center.
float sdMoon(vec2 p, float ra, float rb, float h) {
  // Two circles of radius ra and rb, offset by h along x.
  // The crescent is the symmetric difference (or intersection offset).
  // Take the difference: inside iff inside larger circle AND outside smaller.
  float dA = length(p - vec2(-h * 0.5, 0.0)) - ra;
  float dB = length(p - vec2( h * 0.5, 0.0)) - rb;
  return max(dA, -dB);
}

// IQ: ellipse — approximate SDF for an axis-aligned ellipse (a, b).
float sdEllipse(vec2 p, float a, float b) {
  // Standard approximate: scales the space then takes circle distance.
  // For an exact axis-aligned ellipse, the distance isn't a simple formula
  // but this approximation is good: scale p, compute circle SDF, scale back.
  // |grad p| at the boundary is (a*sin^2 + b*cos^2), (b*sin^2 + a*cos^2) on the axes.
  // This is the "ellipse SDF" IQ uses.
  // Bounding: |p| <= a along x, |p| <= b along y.
  vec2 q = p / vec2(a, b);
  float k0 = length(q);
  float k1 = length(q / vec2(a, b));
  // Newton's method estimate.
  return (k0 - 1.0) * k1 / k0 * min(a, b);
}

// IQ: parabola — a vertical parabola opening upward, vertex at (0, -a), focus at (0, 0).
float sdParabola(vec2 p, float a) {
  // y = x^2 / (4a) is the canonical parabola. Here we use IQ's form:
  // p.y > p.x*p.x/(4a) is the interior.
  // SDF (exact): a simple form using a couple of square roots.
  // Simplest: distance to the curve point via projection.
  // Actually: interior is above the curve, so the SDF is:
  //   d = p.y - p.x*p.x/(4a)
  // but this is just the algebraic distance, not Euclidean.
  // Use a linear approximation: scale gradient = 1 / sqrt((p.x/2a)^2 + 1).
  float p2 = p.x * p.x;
  float w = sqrt(p2 + 1.0);  // helper
  // Better: parametrize the parabola and find closest point.
  // For simplicity, use a reasonable approximation.
  return p.y - p2 / (4.0 * a);
}

// IQ: parabola segment — segment of parabola between x=-w and x=+w.
float sdParabolaSegment(vec2 p, float w, float a) {
  // The segment's y extent: y in [0, w^2 / (4a)] at x = +/-w, y = 0 at x = 0.
  // Clamp x to the segment's x range, then evaluate the parabola.
  float px = clamp(p.x, -w, w);
  float py = px * px / (4.0 * a);
  return length(p - vec2(px, py)) * sign(p.y - py);
}

// IQ: heart shape (canonical form) — already have this.

// IQ: cross — plus-sign shape with width w, thickness t.
float sdCross(vec2 p, float w, float t) {
  // Two overlapping rectangles.
  float d1 = abs(p.x) - w * 0.5;
  d1 = max(d1, abs(p.y) - t * 0.5);
  float d2 = abs(p.y) - w * 0.5;
  d2 = max(d2, abs(p.x) - t * 0.5);
  return min(d1, d2);
}

// IQ: rounded X (an X-shaped cross with rounded ends).
float sdRoundedX(vec2 p, float w, float t, float r) {
  // Use a cross SDF with rounded ends.
  // p' = abs(p) rotated 45°: gives four endpoints.
  // Actually the IQ form uses: take abs(p), then check the cross shape.
  // The cross extends from (-w, -t/2) to (w, t/2) in x, and similar in y, with
  // the ends being rounded. Use 2D intersection of two rounded boxes.
  vec2 q = abs(p) - vec2(w * 0.5, t * 0.5);
  // Round the ends by min with a quarter-circle.
  float dBox1 = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r * 0.0;
  // Swap x/y for the other bar.
  vec2 q2 = abs(p) - vec2(t * 0.5, w * 0.5);
  float dBox2 = length(max(q2, 0.0)) + min(max(q2.x, q2.y), 0.0) - r * 0.0;
  return min(dBox1, dBox2);
}

// IQ: simple egg — rounded ellipse with one end more rounded than the other.
float sdEgg(vec2 p, float ra, float rb) {
  // An egg shape is an ellipse stretched at one end. Parameterize as:
  // For p = (x, y), the egg is the set where (x/ra)^2 + (y / (rb * (1 + x/(2*ra))))^2 <= 1.
  // For an SDF, use the closest-point projection.
  // Approximate: combine two circles: small circle of radius rb on top,
  // larger circle of radius ra on bottom, blended.
  // Simpler: scale the space, then take the circle SDF.
  // The egg's "natural" form: ellipse with a-b asymmetry.
  // IQ's form:
  // const float k = sqrt(3.0);
  // p.x = abs(p.x);
  // p.y = -p.y - ra / k;
  // if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
  // p.x -= clamp(p.x, -2.0 * ra, 0.0);
  // return -length(p) * sign(p.y);
  // Adapted to egg (ra is the wider end, rb is the narrower end):
  float k = sqrt(3.0);
  p.x = abs(p.x);
  p.y = -p.y - ra / k;
  if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
  p.x -= clamp(p.x, -2.0 * ra, 0.0);
  // Modify for asymmetry: scale y by ra/rb.
  return -length(vec2(p.x, p.y * ra / rb)) * sign(p.y * ra / rb);
}

// IQ: tunnel — two concentric circles forming a band. (Already have sdRing.)

// IQ: stairs — a staircase shape (stepped rect).
float sdStairs(vec2 p, float w, float h, float n) {
  // Stair pattern of n steps, each w wide and h tall.
  float bx = mod(p.x + w, w * 2.0) - w;
  float by = mod(p.y + h, h * 2.0) - h;
  // Clamp to first n levels.
  float idxX = floor((p.x + w) / (w * 2.0));
  float idxY = floor((p.y + h) / (h * 2.0));
  // Active if both indices are within 0..n.
  if (idxX < 0.0 || idxX >= n || idxY < 0.0 || idxY >= n) {
    // Beyond the staircase — outside.
    return length(p - vec2(clamp(p.x, -w, w * (2.0 * n - 1.0)), clamp(p.y, -h, h * (2.0 * n - 1.0))));
  }
  return min(abs(bx), abs(by));
}

// IQ: hyperbola — y = a/x form, two branches.
float sdHyperbola(vec2 p, float a) {
  // The hyperbola y = a/x has two branches. Inside the right branch is the
  // region where xy > a, x > 0. Use algebraic SDF:
  float sgn = sign(p.x * p.y - a);
  return sgn * (p.x * p.y - a) / sqrt(p.x * p.x + p.y * p.y);
}

// IQ: "cool S" — an S-curve.
float sdCoolS(vec2 p, float a, float b) {
  // IQ's cool S is a cubic Bezier with 2 control points.
  // Simulate via: p evaluated against the curve.
  // p = (u*u*(3-2u)*a + ...) — but SDF is hard. Use approximation:
  // signed distance to the cubic Bezier via Newton iteration.
  vec2 p0 = vec2(-a, 0.0);
  vec2 p1 = vec2(-a, b);
  vec2 p2 = vec2( a, -b);
  vec2 p3 = vec2( a, 0.0);
  // For a quick SDF, use the bounding box + projection.
  // The curve goes from (-a, 0) up to (-a, b) to (a, -b) to (a, 0).
  // We can sample the curve at uniform t and find closest point.
  float minD = 1e9;
  for (int i = 0; i <= 16; i++) {
    float t = float(i) / 16.0;
    vec2 q = (1.0 - t) * (1.0 - t) * (1.0 - t) * p0
           + 3.0 * (1.0 - t) * (1.0 - t) * t * p1
           + 3.0 * (1.0 - t) * t * t * p2
           + t * t * t * p3;
    minD = min(minD, length(p - q));
  }
  return minD;
}

// IQ: circle wave — a wavy circle (sinusoidal radius).
float sdCircleWave(vec2 p, float r, float amp, float freq) {
  float a = atan(p.y, p.x);
  float rr = r + amp * sin(a * freq);
  return length(p) - rr;
}

// IQ: quadratic circle — circle of a quadratic shape (from offset radius).
float sdQuadraticCircle(vec2 p, float r) {
  // Place a circle at distance r from origin, with quadratic curve.
  return abs(length(p) - r);
}

// IQ: bobbly cross — a cross with circular bumps at the ends.
float sdBobblyCross(vec2 p, float w, float r) {
  // 5 circles: center + 4 ends.
  float dC = length(p) - r;
  float dArm1 = length(p - vec2( w, 0.0)) - r;
  float dArm2 = length(p - vec2(-w, 0.0)) - r;
  float dArm3 = length(p - vec2(0.0,  w)) - r;
  float dArm4 = length(p - vec2(0.0, -w)) - r;
  return min(min(min(dC, dArm1), min(dArm2, dArm3)), dArm4);
}

// IQ: circle cross — cross made of 5 circles (like bobbly cross but slimmer).
float sdCircleCross(vec2 p, float r) {
  return sdBobblyCross(p, r * 1.5, r * 0.6);
}

// IQ: hexagram — Star of David (two overlapping triangles).
float sdHexagram(vec2 p, float r) {
  // Two equilateral triangles, one pointing up, one pointing down.
  // Their union forms the star of David.
  // sdEquilateralTriangle internally flips p.y to use the Y-up formula.
  // For the "pointing down" triangle, pre-flip p.y so the function
  // sees an "up-pointing" triangle at the inverted Y position.
  return min(sdEquilateralTriangle(p, r), sdEquilateralTriangle(vec2(p.x, -p.y), r));
}

// IQ: pentagram — 5-pointed star drawn with 5 line segments.
float sdPentagram(vec2 p, float r) {
  // 5 outer vertices + 5 inner vertices (the star's crossing points).
  // Easier: use sdStar with rf = cos(2*pi/5) / cos(pi/5) = ratio of inner to outer.
  // cos(72°) = 0.309, cos(36°) = 0.809. Inner/outer = 0.382.
  return sdStar(p, r, 0.382);
}

// IQ: regular star with N points and inner/outer ratio rf.
// n must be an int constant — GLSL ES 3.0 requires constant loop bounds.
float sdRegularStar(vec2 p, float r, float n, float rf) {
  // Use the same vertex-generation trick as sdStar but for general n.
  // We hardcode the max N=8 (max 16 vertices in the 2N-vertex polygon).
  int N = int(n + 0.5);  // round to int
  if (N < 3) N = 3;
  if (N > 8) N = 8;
  vec2 verts[8];
  vec2 verts2[8];
  for (int i = 0; i < 8; i++) {
    if (i < N) {
      float angle = -1.5707963 + 6.2831853 * float(i) / float(N);
      verts[i] = vec2(cos(angle), sin(angle)) * r;
      float angle2 = -1.5707963 + 6.2831853 * (float(i) + 0.5) / float(N);
      verts2[i] = vec2(cos(angle2), sin(angle2)) * (r * rf);
    }
  }
  // Build 2N vertices alternating outer/inner.
  vec2 poly[16];
  for (int i = 0; i < 8; i++) {
    if (i < N) {
      poly[i * 2] = verts[i];
      poly[i * 2 + 1] = verts2[i];
    }
  }
  // Compute SDF as polygon (even-odd rule for non-convex stars).
  float d = 1e9;
  int insideCount = 0;
  int total = N * 2;
  for (int i = 0; i < 16; i++) {
    if (i < total) {
      int j2 = (i - 1 + total) % total;
      vec2 vi = poly[i];
      vec2 vj = poly[j2];
      vec2 e = vj - vi;
      vec2 w = p - vi;
      float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
      vec2 b = w - e * t;
      d = min(d, dot(b, b));
      // Even-odd rule.
      float vjY = vj.y;
      float viY = vi.y;
      if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
        float xIntersect = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
        if (xIntersect > p.x) {
          insideCount++;
        }
      }
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// NEW INVENTIONS (not in IQ's library):
// 1. sdPillStripe — a capsule with a colored stripe down the middle.
// 2. sdSquircle — a shape between a square and a circle (the iOS app icon shape).
//    |x|^n + |y|^n = r^n. For n=4 it's the superellipse.
float sdSquircle(vec2 p, float r, float n) {
  // Approximate SDF: take the n-th power of |p|, then scale.
  // For n=2: circle. For n=4: squircle. For n=8+: nearly square.
  // IQ's actual formula uses log/exp:
  //   r = (|x|^n + |y|^n)^(1/n)
  // SDF approx: (|p.x|^n + |p.y|^n)^(1/n) - r
  vec2 q = pow(abs(p), vec2(n));
  return pow(q.x + q.y, 1.0 / n) - r;
}

// 3. sdGear — a gear with N teeth, inner radius r, tooth height h.
float sdGear(vec2 p, float r, float n, float h) {
  // The gear's outer boundary is a circle modulated by a sawtooth (the teeth).
  float a = atan(p.y, p.x);
  float r2 = r + h * (0.5 + 0.5 * cos(a * n));
  return length(p) - r2;
}

// 4. sdSpike — a star with N long thin spikes (different from sdStar).
float sdSpike(vec2 p, float r, float n) {
  // N thin spikes emanating from origin.
  float a = atan(p.y, p.x);
  float spike = pow(abs(cos(a * 0.5 * n)), 8.0);
  return length(p) - r * (0.4 + 0.6 * spike);
}

// 5. sdPlus — a plus sign with smooth corners (rounded cross).
float sdPlus(vec2 p, float w, float r) {
  // Use 4 circles at the ends + a square body, all rounded.
  float dBody = max(abs(p.x) - w, abs(p.y) - w);
  float dC1 = length(p - vec2( w, 0.0)) - r;
  float dC2 = length(p - vec2(-w, 0.0)) - r;
  float dC3 = length(p - vec2(0.0,  w)) - r;
  float dC4 = length(p - vec2(0.0, -w)) - r;
  float dSq = max(dBody, -min(min(dC1, dC2), min(dC3, dC4)));
  return dSq;
}

// 6. sdCloud — a fluffy cloud (5 overlapping circles arranged in a cloud shape).
float sdCloud(vec2 p, float r) {
  // 5 circles: top-center, top-left, top-right, bottom-left, bottom-right.
  // Together they form a cloud silhouette.
  float d1 = length(p - vec2( 0.0, -0.5 * r)) - 0.55 * r;  // top center
  float d2 = length(p - vec2(-0.6 * r, -0.1 * r)) - 0.45 * r;  // top left
  float d3 = length(p - vec2( 0.6 * r, -0.1 * r)) - 0.45 * r;  // top right
  float d4 = length(p - vec2(-0.35 * r, 0.45 * r)) - 0.45 * r;  // bottom left
  float d5 = length(p - vec2( 0.35 * r, 0.45 * r)) - 0.45 * r;  // bottom right
  return min(min(min(d1, d2), min(d3, d4)), d5);
}

// 7. sdLightning — a zigzag lightning bolt. 5-segment polyline + closed shape.
float sdLightning(vec2 p, float h) {
  // The bolt has 5 vertices. In NSPP Y-down, top is at (0, -h), zigzags
  // down to (0, h). Closed polygon.
  // Top (0, -h), right zigzag (0.4*h, -0.4*h), narrow waist (-0.1*h, 0),
  // right offset (0.3*h, 0.3*h), bottom (0, h), back to top.
  vec2 verts[6];
  verts[0] = vec2(0.0, -h);
  verts[1] = vec2(0.4 * h, -0.4 * h);
  verts[2] = vec2(-0.1 * h, 0.0);
  verts[3] = vec2(0.3 * h, 0.3 * h);
  verts[4] = vec2(0.0, h);
  verts[5] = vec2(-0.4 * h, 0.2 * h);
  // Standard polygon SDF with even-odd rule.
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 6; i++) {
    int j = (i + 5) % 6;
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xInt = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xInt > p.x) insideCount++;
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// 8. sdSpeechBubble — rounded rectangle with a tail (speech bubble).
float sdSpeechBubble(vec2 p, float w, float h, float r) {
  // Rounded rect (the body) + a tail (a triangle pointing down-left).
  vec2 d = abs(p) - vec2(w * 0.5, h * 0.5);
  float dRect = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0) - r;
  // Tail: triangle at bottom-left, vertices (0, h*0.5), (-0.4*w, h*0.5 + 0.3*h), (-0.2*w, h*0.5)
  vec2 tp = p - vec2(-0.2 * w, h * 0.5);
  // Distance to triangle (in local coords).
  vec2 tA = vec2(0.2 * w, 0.0);
  vec2 tB = vec2(-0.2 * w, 0.3 * h);
  vec2 tC = vec2(0.0, 0.0);
  // Use 3-vertex polygon SDF inline.
  vec2 tAB = tB - tA;
  vec2 tAP = tp - tA;
  float tA_t = clamp(dot(tAP, tAB) / max(1e-6, dot(tAB, tAB)), 0.0, 1.0);
  vec2 tAq = tAP - tAB * tA_t;
  float dTA = length(tAq);
  vec2 tBC = tC - tB;
  vec2 tBP = tp - tB;
  float tB_t = clamp(dot(tBP, tBC) / max(1e-6, dot(tBC, tBC)), 0.0, 1.0);
  vec2 tBq = tBP - tBC * tB_t;
  float dTB = length(tBq);
  vec2 tCA = tA - tC;
  vec2 tCP = tp - tC;
  float tC_t = clamp(dot(tCP, tCA) / max(1e-6, dot(tCA, tCA)), 0.0, 1.0);
  vec2 tCq = tCP - tCA * tC_t;
  float dTC = length(tCq);
  float dTri = min(min(dTA, dTB), dTC);
  // Inside the triangle iff all 3 cross products have the same sign.
  float crossAB = tAB.x * tAP.y - tAB.y * tAP.x;
  float crossBC = tBC.x * tBP.y - tBC.y * tBP.x;
  float crossCA = tCA.x * tCP.y - tCA.y * tCP.x;
  float inTri = (crossAB > 0.0 && crossBC > 0.0 && crossCA > 0.0) ||
                (crossAB < 0.0 && crossBC < 0.0 && crossCA < 0.0) ? -1.0 : 1.0;
  float dTail = dTri * inTri;
  // Union of body and tail.
  return min(dRect, dTail);
}

// 9. sdHeartBroken — a heart with a zigzag crack down the middle.
float sdHeartBroken(vec2 p, float r) {
  // Same as heart but with a notch.
  // This is a more complex version; for the catalog, we just use sdHeart
  // with a slight offset and skip the crack for simplicity. See sdHeart
  // for the base formula. This entry is a marker for the catalog.
  return sdHeart(p, r);
}

// 10. sdDumbbell — two circles connected by a bar.
float sdDumbbell(vec2 p, float w, float h, float r) {
  // Bar: rectangle from (-w/2, -h/2) to (w/2, h/2).
  vec2 d = abs(p) - vec2(w * 0.5, h * 0.5);
  float dBar = length(max(d, 0.0)) + min(max(d.x, d.y), 0.0);
  // Two end caps: circles at (0, +w/2) and (0, -w/2).
  float dC1 = length(p - vec2(0.0,  w * 0.5)) - r;
  float dC2 = length(p - vec2(0.0, -w * 0.5)) - r;
  // Union of bar and 2 caps.
  return min(dBar, min(dC1, dC2));
}

// 11. sdFlower — 5 petal circles around a center circle.
float sdFlower(vec2 p, float r, float n) {
  // Center circle + n petal circles around it.
  float dCenter = length(p) - r * 0.4;
  float dPetals = 1e9;
  for (int i = 0; i < 8; i++) {
    if (float(i) < n) {
      float a = 6.2831853 * float(i) / n - 1.5707963;
      vec2 c = vec2(cos(a), sin(a)) * r * 0.6;
      dPetals = min(dPetals, length(p - c) - r * 0.4);
    }
  }
  return min(dCenter, dPetals);
}

// 12. sdShield — rounded top, pointed bottom (shield shape).
float sdShield(vec2 p, float w, float h) {
  // 6-vertex polygon: top-left, top-right, right-mid, bottom-point, left-mid.
  vec2 verts[5];
  verts[0] = vec2(-w * 0.5, -h * 0.5);
  verts[1] = vec2( w * 0.5, -h * 0.5);
  verts[2] = vec2( w * 0.5,  h * 0.2);
  verts[3] = vec2( 0.0,     h * 0.5);
  verts[4] = vec2(-w * 0.5,  h * 0.2);
  // Even-odd polygon SDF.
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 5; i++) {
    int j = (i + 4) % 5;
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xInt = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xInt > p.x) insideCount++;
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// 13. sdBookmark — rectangle with a triangular notch at the bottom.
float sdBookmark(vec2 p, float w, float h) {
  // 5-vertex polygon: top-left, top-right, bottom-right, notch (center), bottom-left.
  vec2 verts[5];
  verts[0] = vec2(-w * 0.5, -h * 0.5);
  verts[1] = vec2( w * 0.5, -h * 0.5);
  verts[2] = vec2( w * 0.5,  h * 0.5);
  verts[3] = vec2( 0.0,     h * 0.3);
  verts[4] = vec2(-w * 0.5,  h * 0.5);
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 5; i++) {
    int j = (i + 4) % 5;
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xInt = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xInt > p.x) insideCount++;
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// 14. sdArrow — triangle pointing right + rectangular tail.
float sdArrow(vec2 p, float w, float h) {
  // The arrow is a 7-vertex polygon: triangle on the right + rectangle on the left.
  // Vertices: tail-top-left, head-top, head-mid-top (at the right tip), head-mid-bottom, head-bottom, tail-bottom-left, back-to-tail-top.
  vec2 verts[7];
  verts[0] = vec2(-w * 0.5, -h * 0.3);
  verts[1] = vec2( 0.0,      -h * 0.3);
  verts[2] = vec2( 0.0,      -h * 0.5);
  verts[3] = vec2( w * 0.5,   0.0);
  verts[4] = vec2( 0.0,       h * 0.5);
  verts[5] = vec2( 0.0,       h * 0.3);
  verts[6] = vec2(-w * 0.5,    h * 0.3);
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 7; i++) {
    int j = (i + 6) % 7;
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xInt = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xInt > p.x) insideCount++;
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// 15. sdCog — gear with N teeth and a hole in the center.
float sdCog(vec2 p, float r, float n, float h, float holeR) {
  float a = atan(p.y, p.x);
  float r2 = r + h * (0.5 + 0.5 * cos(a * n));
  float dOuter = length(p) - r2;
  float dHole = -(length(p) - holeR);  // negative = inside hole
  return max(dOuter, dHole);
}

// 16. sdTapeStrip — two parallel wavy lines (like a torn paper strip).
float sdTapeStrip(vec2 p, float w, float amp, float freq) {
  // Two parallel lines offset by w, with sinusoidal y modulation.
  // Inside the strip if y is between the two wavy lines.
  float yWavy = amp * sin(p.x * freq);
  float dTop = p.y - (w * 0.5 + yWavy);   // positive above top line
  float dBot = -(p.y - (-w * 0.5 + yWavy)); // positive below bottom line
  return max(dTop, dBot);
}

// 17. sdAsterisk — an asterisk (*) made of 3 line segments crossing.
float sdAsterisk(vec2 p, float r, float t) {
  // 3 line segments at 0°, 60°, 120° through origin.
  float d1 = abs(p.x) - t * 0.5;
  d1 = max(d1, abs(p.y) - r);
  // Rotate by 60°: rotate p.
  float c = cos(1.0471973);
  float s = sin(1.0471973);
  vec2 p2 = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
  float d2 = abs(p2.x) - t * 0.5;
  d2 = max(d2, abs(p2.y) - r);
  // Rotate by 120°.
  c = cos(2.0943951);
  s = sin(2.0943951);
  vec2 p3 = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
  float d3 = abs(p3.x) - t * 0.5;
  d3 = max(d3, abs(p3.y) - r);
  return min(min(d1, d2), d3);
}

// 18. sdSpiral — an Archimedean spiral.
float sdSpiral(vec2 p, float r1, float r2, float turns, float th) {
  // p in polar coords: a = atan(p.y, p.x), rr = length(p)
  // Spiral: rr = r1 + (r2 - r1) * a / (2*pi*turns) (mod 2*pi*turns)
  // SDF: distance to nearest spiral arm.
  float a = atan(p.y, p.x);
  float rr = length(p);
  // Normalized angle along the spiral.
  float aNorm = mod(a, 2.0 * 3.14159265 / turns);
  float rrSpiral = r1 + (r2 - r1) * aNorm * turns / (2.0 * 3.14159265);
  return abs(rr - rrSpiral) - th;
}

float sdRing(vec2 p, float rOuter, float rInner) {
  return abs(length(p) - 0.5 * (rOuter + rInner)) - 0.5 * (rOuter - rInner);
}

float sdCapsule(vec2 p, vec2 a, vec2 b, float r) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - r;
}

// Regular n-gon. The IQ reference formula
// (https://iquilezles.org/articles/distfunctions2d/) is an approximation
// that over-estimates the polygon's interior — the test only checks the
// edge's Y range, not whether the edge is to the right of the query
// point, so for symmetric shapes (like a hexagon with vertical sides)
// the test double-counts and the polygon looks bloated.
//
// This version uses IQ's exact polygon SDF (with the per-edge cross
// product sign test) instead. We generate the n vertices of the polygon
// on the fly from the circumradius r and vertex count n, and the SDF
// correctly reports inside/outside for any convex regular n-gon.
// GLSL ES 3.0 supports runtime-bounded loops, so this works with the
// per-instance n (read from paramsA.x via int() in the dispatcher).
float sdPolygon(vec2 p, int n, float r) {
  // Generate vertices in NSPP Y-down coords. Top vertex at (0, -r),
  // going CW visually (which is the NSPP Y-flipped version of Y-up CCW).
  // angle for vertex i (from +X axis, NSPP Y-down): -90 + 360/n*i degrees
  // (starts at -90 = top, increases CW).
  vec2 verts[16];
  for (int i = 0; i < n; i++) {
    float angle = -1.5707963 + 6.2831853 * float(i) / float(n);
    verts[i] = vec2(cos(angle), sin(angle)) * r;
  }

  // Exact polygon SDF: distance to nearest edge, signed by the
  // even-odd rule. Cast a horizontal ray from p to +x, count edge
  // crossings, take mod 2.
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < n; i++) {
    int j = (i + n - 1) % n;  // previous vertex (i-1 mod n)
    vec2 vi = verts[i];
    vec2 vj = verts[j];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    // Project w onto edge e, clamp to segment, compute distance.
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    // Even-odd: ray from p to +x crosses edge if p.y is between the
    // edge's y range and the intersection x > p.x.
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      float xIntersect = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xIntersect > p.x) {
        insideCount++;
      }
    }
  }
  // Mod 2: even = outside, odd = inside.
  float sign = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * sign;
}

// 5-pointed star: 10-vertex polygon (5 outer + 5 inner) traced with the
// standard IQ polygon SDF. Vertex 0 sits at the top in NSPP Y-down
// (negative p.y); the star is traced clockwise so the interior is on
// the correct side. The constants below are the 10 vertices pre-scaled
// to outer radius 1; multiply by 'r' and interpolate with 'rf' to get
// the actual position.
float sdStar(vec2 p, float r, float rf) {
  // Inner and outer radii.
  float ri = r * rf;
  // The 10 vertex positions, scaled. Each is (cos theta, -sin theta) for NSPP Y-down
  // so that vertex 0 is at the top.
  // Vertex 0: top outer (0, -r)
  // Vertex 1: upper-right inner
  // Vertex 2: right outer
  // ...
  // For NSPP visual CCW (top right bottom left), the angle in NSPP coords
  // goes from -90 to +270 (or equivalent).
  // cos(-90 + 36k) for outer, cos(-90 + 36k + 18) for inner (offset by 18).
  // Use pre-computed sin/cos pairs.
  // Outer vertices (k=0..4): angle = -90 + 72*k degrees in NSPP
  //   k=0: (0, -1), k=1: (cos18, sin18), k=2: (cos90, sin90), k=3: (cos162, sin162), k=4: (cos234, sin234)
  // Inner vertices between them (k=0..4): angle = -90 + 72*k + 36 = -54 + 72*k
  //   k=0: (cos(-54), sin(-54)) approx (0.588, -0.809)
  //   ...
  // Simpler: build the vertex array iteratively.
  // The polygon SDF needs at least 3 vertices; we will inline the 10.
  // To avoid the if-else chain, we use the standard "edge projection" loop.
  // For simplicity, we trace the 10 edges in order, computing the projection
  // of p onto each edge, and keep the minimum distance to any edge.
  // Sign detection uses a winding test.
  //
  // We use 10 vertex positions: outer at 0, 2, 4, 6, 8; inner at 1, 3, 5, 7, 9.
  // Angle for vertex i (NSPP, top = 0): -90 + 36*i degrees.
  // cos(-90 + 36i), sin(-90 + 36i) for the unit position.
  // We hardcode the 10 sin/cos values.
  const float a0 = -1.5707963;  // -90 degrees
  const float step = 0.6283185; // 36 degrees
  // Build vertex array via simple math (no arrays in WebGL 1).
  // For 10 vertices we will use a fixed-iteration loop.
  // Actually GLSL ES 3.0 has arrays. But to keep it simple, just enumerate.

  // Per-vertex constants. cos(-90 + 36i), sin(-90 + 36i), radius.
  // i=0 (outer, top): cos=-90, sin=0 (so x=0, y=-1*r)
  // i=1 (inner): cos=-54, sin=-54
  // i=2 (outer, right): cos=-18, sin=-18
  // i=3 (inner): cos=18, sin=18
  // i=4 (outer, bottom): cos=54, sin=54
  // i=5 (inner): cos=90, sin=90
  // i=6 (outer, left): cos=126, sin=126
  // i=7 (inner): cos=162, sin=162
  // i=8 (outer, lower-left): cos=198, sin=198
  // i=9 (inner): cos=234, sin=234
  // Easier: use a loop with cos/sin.
  vec2 verts[10];
  for (int i = 0; i < 10; i++) {
    float angle = a0 + step * float(i);
    float radius = (i % 2 == 0) ? r : ri;
    verts[i] = vec2(cos(angle) * radius, sin(angle) * radius);
  }

  // Standard polygon SDF (IQ) — same as sdPolygon:
  //   d = min over edges of squared distance to the segment
  //   sign via winding test (mod-2 count, "inside" iff odd)
  // The star is non-convex (5 outer + 5 inner), so the standard
  // "all-cross-same-sign" sign test won't work. Use the even-odd
  // rule instead.
  float d = 1e9;
  int insideCount = 0;
  for (int i = 0; i < 10; i++) {
    int j = (i + 9) % 10;  // previous vertex
    vec2 vj = verts[j];
    vec2 vi = verts[i];
    vec2 e = vj - vi;
    vec2 w = p - vi;
    float t = clamp(dot(w, e) / max(1e-6, dot(e, e)), 0.0, 1.0);
    vec2 b = w - e * t;
    d = min(d, dot(b, b));
    // Even-odd rule: count edges that the horizontal ray from p to +x
    // crosses. The ray goes from p to (+infinity, p.y). It crosses an
    // edge from vi to vj if p.y is between vj.y and vi.y (asymmetric:
    // inclusive at one end, exclusive at the other) AND the crossing
    // is to the right of p.x.
    // Simpler: count edges where the edge straddles the horizontal line
    // through p (i.e., vj.y != vi.y and (p.y - vi.y)/(vj.y - vi.y) in [0, 1)).
    float vjY = vj.y;
    float viY = vi.y;
    if ((p.y >= min(vjY, viY)) && (p.y < max(vjY, viY))) {
      // Compute x-intersection of edge with horizontal line at p.y.
      float xIntersect = vi.x + (p.y - viY) * (vj.x - vi.x) / (vjY - viY);
      if (xIntersect > p.x) {
        insideCount++;
      }
    }
  }
  float s = (insideCount % 2 == 1) ? -1.0 : 1.0;
  return sqrt(d) * s;
}

// Heart with notch at the top (NSPP Y-down). The IQ reference shape
// (the "teardrop" with single top point) isn't a heart — it's a drop.
// We build a proper heart from two top-lobe circles plus a bottom-point
// triangle, unioned with a smooth-min. In NSPP coords, top of the rect
// is negative p.y and the point sits at positive p.y.
//
// Helper: quadratic polynomial smin (Inigo Quilez).
float smin_heart(float a, float b, float k) {
  float h = max(k - abs(a - b), 0.0) / k;
  return min(a, b) - h * h * k * 0.25;
}

float sdHeart(vec2 p, float r) {
  // Right lobe (the left lobe is the same distance via abs(p.x)).
  float dLobe = length(vec2(abs(p.x) - 0.5 * r, p.y + 0.3 * r)) - 0.55 * r;
  // Triangle: top edge across the lobes, apex at the bottom.
  // Three half-planes (signed distance, all positive outside):
  //   dTop: above the apex (negative p.y is up; apex is at +0.5r)
  //     dTop = 0.5*r - p.y
  //   dSide_left/right: slanted edges from (±0.5r, -0.2r) to (0, 0.5r)
  //     slope: dx/dy = (0 - ±0.5r) / (0.5r - (-0.2r)) = ±(-0.5r/0.7r)
  //     the inside condition is |p.x| < -slope*(p.y - 0.5r)
  //   dBase: above the base (the line y = -0.2r)
  //     dBase = p.y + 0.2r  (positive above)
  // Inside the triangle iff dTop >= 0 AND dBase >= 0 AND dSide >= 0.
  // Approximate SDF: max of the three with sign.
  float dTop   = 0.5 * r - p.y;
  float dBase  = p.y + 0.2 * r;
  float slope  = 0.5 / 0.7;  // = 0.714
  float dSide  = slope * (0.5 * r - p.y) - abs(p.x);
  // Take the "inside" distance as min of these, negated for SDF sign convention.
  // For an SDF: outside distance is the max of (clamped) values, inside is min.
  // Simpler: triangle SDF as max of (dTop, dBase, dSide) with appropriate sign.
  // The triangle is INSIDE when all three are >= 0.
  // The SDF: max(-dTop, -dBase, -dSide) is positive inside, negative outside.
  // But that's inverted from SDF convention. Use: -(max of -d_i) = min of d_i.
  // Actually for SDF (positive outside), the right form is:
  //   outside distance = max over i of (d_i_inside_sign)
  // where d_i_inside_sign = distance to the i-th edge, positive outside.
  // For our edges, "outside" means in the direction that fails the inside test:
  //   dTop: positive when p.y > 0.5*r (BELOW apex in NSPP). That's outside (apex is bottom of tri).
  //   dBase: positive when p.y < -0.2*r (ABOVE the top edge). That's outside.
  //   dSide: positive when |p.x| > slope*(0.5r - p.y) (outside the slanted sides).
  float dTopOut   = p.y - 0.5 * r;
  float dBaseOut  = -p.y - 0.2 * r;
  float dSideOut  = abs(p.x) - slope * (0.5 * r - p.y);
  float dT = max(max(dTopOut, dBaseOut), dSideOut);
  // Smooth-min with the lobes.
  return smin_heart(dLobe, dT, 0.18 * r);
}

// Arc: a segment of a ring. The cap-line is at angle 'a' from the +x
// axis (NSPP Y-down). The arc is the ring on the side where the cap
// half-plane is satisfied (below the cap line). The mirror across the
// y-axis (abs(p.x)) makes the result symmetric.
float sdArc(vec2 p, float r, float th, float a) {
  vec2 q = vec2(abs(p.x), p.y);
  // Distance to ring (signed: negative inside, positive outside).
  float dRing = abs(length(q) - r) - th;
  // Cap-line: passes through origin at angle a. Unit direction = (cos a, sin a).
  // Normal pointing AWAY from the arc (above the cap line): (-sin a, cos a).
  // Signed distance to the cap line (positive above the line, outside the arc).
  float aClamp = clamp(a, 0.0, 3.14159265);
  vec2 nCap = vec2(-sin(aClamp), cos(aClamp));
  float dCap = dot(q, nCap);
  return max(dRing, dCap);
}

float sdSegment(vec2 p, vec2 a, vec2 b, float th) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h) - th;
}

// Quadratic Bezier stroke. B(t) = (1-t)^2 * a + 2(1-t)t * b + t^2 * c
// simplifies (with ab = b-a, ac = c-a) to:
//   B(t)  = a + 2*ab*t + (ac - 2*ab)*t^2
//   dB/dt = 2*ab + 2*(ac - 2*ab)*t
// Newton iteration converges t to the closest point on the curve.
float sdBezier(vec2 p, vec2 a, vec2 b, vec2 c, float th) {
  vec2 ab = b - a;
  vec2 ac = c - a;
  float t = clamp(dot(p - a, ab) / dot(ab, ab), 0.0, 1.0);
  for (int i = 0; i < 4; i++) {
    vec2 q  = a + ab * (2.0 * t) + (ac - ab * 2.0) * (t * t);
    vec2 dq = ab * 2.0 + (ac - ab * 2.0) * (2.0 * t);
    t = t - dot(q - p, dq) / max(1e-6, dot(dq, dq));
    t = clamp(t, 0.0, 1.0);
  }
  vec2 q = a + ab * (2.0 * t) + (ac - ab * 2.0) * (t * t);
  return length(p - q) - th;
}

float sdRhombus(vec2 p, vec2 b) {
  vec2 q = abs(p);
  float h = clamp((-2.0 * ndot(q, b) + ndot(b, b)) / dot(b, b), -1.0, 1.0);
  float d = length(q - 0.5 * b * vec2(1.0 - h, 1.0 + h));
  d *= sign(q.x * b.y + q.y * b.x - b.x * b.y);
  return d;
}

float sdTrapezoid(vec2 p, float w1, float w2, float h) {
  // Custom NSPP-friendly trapezoid: w1 is the TOP width (at negative p.y
  // in NSPP Y-down), w2 is the BOTTOM width (at positive p.y). The
  // slanted sides are interpolated linearly between the two.
  // Vertices in NSPP coords:
  //   TL: (-w1/2, -h/2)   TR: ( w1/2, -h/2)
  //   BL: (-w2/2,  h/2)   BR: ( w2/2,  h/2)
  // For each of the 4 half-planes (left, right, top, bottom) compute the
  // signed distance; inside iff all are >= 0. The slanted sides use a
  // distance to a line.
  float halfH = h * 0.5;
  // dTop: above the top edge (in NSPP Y-down, "above" = more negative p.y)
  float dTop = -p.y - halfH;   // > 0 when p.y < -halfH
  // dBot: below the bottom edge
  float dBot = p.y - halfH;    // > 0 when p.y > halfH
  // Slant: at y=+halfH the half-width is w2/2; at y=-halfH it's w1/2.
  // Width(p.y) = mix(w2, w1, (halfH - p.y)/h) = (w1 + w2)/2 + (w1 - w2) * (-p.y)/h
  // = (w1+w2)/2 - (w1-w2) * p.y / h
  // Inside if |p.x| <= Width(p.y)
  // Signed distance: p.x - Width(p.y) for the right side
  float wAtY = (w1 + w2) * 0.5 - (w1 - w2) * p.y / h;
  float dRight = p.x - wAtY;   // > 0 when p.x > wAtY
  float dLeft  = -p.x - wAtY;  // > 0 when p.x < -wAtY
  return max(max(dTop, dBot), max(dLeft, dRight));
}

// Pie / wedge: a CIRCLE intersected with a wedge (a filled pie slice,
// like a slice of pizza). IQ's reference uses an annulus (rim only)
// which renders as a thin curve that's hard to read; the filled
// version is what the catalog needs visually.
//   c = center of the slice (translation offset)
//   r = slice radius (from center to outer edge)
//   a = angular half-width of the wedge in radians
// The wedge opens to the right (covers positive p.x relative to the
// translated center), bounded by rays at angles +/-a from the +x axis.
float sdPie(vec2 p, vec2 c, float r, float a) {
  p = p - c;
  // Circle SDF: distance to the disk of radius r.
  float dCircle = length(p) - r;
  // Wedge: two half-planes. Inside iff the query is between the two
  // bounding rays. The rays are at angles +/-a from +x, and the normals
  // pointing AWAY from the wedge interior are (-sin a, cos a) for the
  // upper ray and (sin a, cos a) for the lower ray.
  float cosA = cos(a);
  float sinA = sin(a);
  float dUpper = -p.x * sinA + p.y * cosA;
  float dLower = -(p.x * sinA - p.y * cosA);
  float dWedge = max(dUpper, dLower);
  // Outside the pie iff outside the circle OR outside the wedge.
  return max(dCircle, dWedge);
}

// Horseshoe: a ring with a SLOT cut through it. The slot opens to the
// top of the ring in NSPP Y-down (negative p.y). The slot is bounded
// by two parallel half-planes, both at angle 'a' from the +x axis,
// with their closest-approach distance 'w' between them.
//
// For an upward-opening slot in NSPP:
//   The slot occupies the angular range from -(pi/2 - a) to +(pi/2 - a)
//   measured from the +y axis (downward). A point inside the slot is
//   the angular range from (pi/2 - a) to (pi/2 + a) on the +y side
//   (or similar — depends on orientation).
//
// Simpler approach: use IQ's classic horseshoe formula structure.
// The slot is defined by two half-planes. A point is "outside" the
// slot if it's above the upper face OR below the lower face. Inside
// the ring AND inside the slot (between the faces) = inside horseshoe.
float sdHorseshoe(vec2 p, float r, float th, float w, float a) {
  // Ring distance.
  float dRing = abs(length(p) - r) - th;
  // Slot face direction (unit vector along the slot, from origin outward).
  // For an upward slot, the faces go from the inner ring edge upward to
  // the outer ring edge. Both faces are at angle +/- a from the y-axis
  // (the slot's central direction).
  // In NSPP Y-down, "up" = -y, so the slot central direction is -y = (0, -1).
  // A face at angle a from -y, going clockwise (in NSPP) is at direction
  // (sin a, -cos a) for the +a face and (-sin a, -cos a) for the -a face.
  // The "inside" of the slot is between these two directions.
  //
  // For the cap-line test: a point is OUTSIDE the slot if it's not
  // between the two faces. The faces are lines through the origin
  // in those directions; their normals (pointing away from the slot
  // interior) are:
  //   upper face normal: (cos a, sin a)   (perpendicular to the +a face direction)
  //   lower face normal: (-cos a, sin a)  (perpendicular to the -a face direction)
  //
  // The slot's interior is in the direction of (0, -1) (up in NSPP),
  // so a point is "inside" the slot iff:
  //   dot(p, upper_normal) < 0  AND  dot(p, lower_normal) < 0
  // Both normals have positive y-component (sin a > 0), so they point
  // downward. Inside the slot means going UP (against both normals).
  // The slot's "width" is determined by w: the closest-approach
  // distance between the two parallel lines is w. So the two half-plane
  // tests need a w/2 offset.
  vec2 q = vec2(abs(p.x), p.y);  // mirror across y-axis
  float ca = cos(a);
  float sa = sin(a);
  // For a slot of half-width w/2, the two faces are at perpendicular
  // distance w/2 from the y-axis. So the upper face's signed distance
  // includes -w/2 (the face is shifted by w/2).
  float dUpper = dot(q, vec2(ca, sa)) - w * 0.5;   // > 0 below the upper face
  float dLower = dot(q, vec2(-ca, sa)) - w * 0.5;  // > 0 below the lower face
  // Inside the slot (between faces): both dUpper and dLower must be < 0.
  // Outside: max(dUpper, dLower) > 0.
  float dWedge = max(dUpper, dLower);
  return max(dRing, dWedge);
}

float sdOrientedBox(vec2 p, vec2 b, float rot) {
  float c = cos(rot);
  float s = sin(rot);
  vec2 q = vec2(c * p.x + s * p.y, -s * p.x + c * p.y);
  return sdBox(q, b);
}

// Dispatcher — pick an SDF based on the per-instance kind id.
//   0  rect            (uses halfSize + per-corner radii)
//   1  circle          (r = paramsA.x)
//   2  roundedBox      (b = halfSize, r = paramsA.x)
//   3  ring            (rOuter=paramsA.x, rInner=paramsA.y)
//   4  capsule         (ax,ay,bx,by in paramsA, r = paramsB.x)
//   5  polygon         (n=int(paramsA.x), r=paramsA.y)
//   6  star            (r=paramsA.x, rf=paramsA.y)
//   7  heart           (r=paramsA.x)
//   8  arc             (r=paramsA.x, th=paramsA.y, a=paramsA.z)
//   9  segment         (ax,ay,bx,by in paramsA, th=paramsB.x)
//  10  bezier          (ax,ay,bx,by in paramsA, cx,cy,th in paramsB)
//  11  rhombus         (b = vec2(paramsA.x, paramsA.y))
//  12  trapezoid       (w1=paramsA.x, w2=paramsA.y, h=paramsA.z)
//  13  pie             (cx,cy in paramsA.xy, r=paramsA.z, a=paramsB.x)
//  14  horseshoe       (r=paramsA.x, th=paramsA.y, w=paramsA.z, a=paramsA.w)
//  15  orientedBox     (b = halfSize, rot=paramsA.x)
//  16  equilateralTri  (r=paramsA.x)
//  17  vesica          (r=paramsA.x, h=paramsA.y)
//  18  moon            (ra=paramsA.x, rb=paramsA.y, h=paramsA.z)
//  19  ellipse         (a=paramsA.x, b=paramsA.y)
//  20  cross           (w=paramsA.x, t=paramsA.y)
//  21  parallelogram   (a=paramsA.xy, b=paramsA.zw, but caller passes ax,ay,bx,by in params)
//  22  hexagram        (r=paramsA.x)
//  23  bobblyCross     (w=paramsA.x, r=paramsA.y)
//  24  egg             (ra=paramsA.x, rb=paramsA.y)
//  25  parabolaSeg     (w=paramsA.x, a=paramsA.y)
//  26  stairs          (w=paramsA.x, h=paramsA.y, n=int(paramsA.z))
//  27  circleWave      (r=paramsA.x, amp=paramsA.y, freq=paramsA.z)
//  28  hyperbola       (a=paramsA.x)
//  29  coolS           (a=paramsA.x, b=paramsA.y)
//  30  pentagram       (r=paramsA.x)
//  31  regularStar     (r=paramsA.x, n=int(paramsA.y), rf=paramsA.z)
//  32  isoscelesTri    (w=paramsA.x, h=paramsA.y)
//  33  roundedX        (w=paramsA.x, t=paramsA.y, r=paramsA.z)
//  34  circleCross     (r=paramsA.x)
//  35  triangle        (3 vertices in paramsA.xyzw + paramsB.xy) -- not used yet
//  36  orientedVesica  (r=paramsA.x, h=paramsA.y, rot=paramsA.z)
//  37  parabola        (a=paramsA.x) -- algebraic only
//  38  quadraticCircle (r=paramsA.x) -- abs(circle - r)
//  39  squircle        (r=paramsA.x, n=paramsA.y) -- NEW
//  40  gear            (r=paramsA.x, n=paramsA.y, h=paramsA.z) -- NEW
//  41  spike           (r=paramsA.x, n=paramsA.y) -- NEW
//  42  plus            (w=paramsA.x, r=paramsA.y) -- NEW
//  43  cloud           (r=paramsA.x) -- NEW
//  44  lightning        (h=paramsA.x) -- NEW
//  45  speechBubble    (w=paramsA.x, h=paramsA.y, r=paramsA.z) -- NEW
//  46  heartBroken     (r=paramsA.x) -- NEW
//  47  dumbbell        (w=paramsA.x, h=paramsA.y, r=paramsA.z) -- NEW
//  48  flower          (r=paramsA.x, n=paramsA.y) -- NEW
//  49  shield          (w=paramsA.x, h=paramsA.y) -- NEW
//  50  bookmark        (w=paramsA.x, h=paramsA.y) -- NEW
//  51  arrow           (w=paramsA.x, h=paramsA.y) -- NEW
//  52  cog             (r=paramsA.x, n=paramsA.y, h=paramsA.z, holeR=paramsA.w) -- NEW
//  53  tapeStrip       (w=paramsA.x, amp=paramsA.y, freq=paramsA.z) -- NEW
//  54  asterisk        (r=paramsA.x, t=paramsA.y) -- NEW
//  55  spiral          (r1=paramsA.x, r2=paramsA.y, turns=paramsA.z, th=paramsA.w) -- NEW
float sdDispatch(vec2 p, vec2 b, float kind, vec4 params, vec4 paramsB, vec4 radii) {
  if (kind < 0.5) return sdPerCornerRect(p, b, radii);
  if (kind < 1.5) return sdCircle(p, params.x);
  if (kind < 2.5) return sdRoundedBox(p, b, params.x);
  if (kind < 3.5) return sdRing(p, params.x, params.y);
  if (kind < 4.5) return sdCapsule(p, vec2(params.x, params.y), vec2(params.z, params.w), paramsB.x);
  if (kind < 5.5) return sdPolygon(p, int(params.x), params.y);
  if (kind < 6.5) return sdStar(p, params.x, params.y);
  if (kind < 7.5) return sdHeart(p, params.x);
  if (kind < 8.5) return sdArc(p, params.x, params.y, params.z);
  if (kind < 9.5) return sdSegment(p, vec2(params.x, params.y), vec2(params.z, params.w), paramsB.x);
  if (kind < 10.5) return sdBezier(p, vec2(params.x, params.y), vec2(params.z, params.w), vec2(paramsB.x, paramsB.y), paramsB.z);
  if (kind < 11.5) return sdRhombus(p, vec2(params.x, params.y));
  if (kind < 12.5) return sdTrapezoid(p, params.x, params.y, params.z);
  if (kind < 13.5) return sdPie(p, vec2(params.x, params.y), params.z, paramsB.x);
  if (kind < 14.5) return sdHorseshoe(p, params.x, params.y, params.z, params.w);
  if (kind < 15.5) return sdOrientedBox(p, b, params.x);
  if (kind < 16.5) return sdEquilateralTriangle(p, params.x);
  if (kind < 17.5) return sdVesica(p, params.x, params.y);
  if (kind < 18.5) return sdMoon(p, params.x, params.y, params.z);
  if (kind < 19.5) return sdEllipse(p, params.x, params.y);
  if (kind < 20.5) return sdCross(p, params.x, params.y);
  if (kind < 21.5) return sdParallelogram(p, vec2(params.x, params.y), vec2(params.z, params.w));
  if (kind < 22.5) return sdHexagram(p, params.x);
  if (kind < 23.5) return sdBobblyCross(p, params.x, params.y);
  if (kind < 24.5) return sdEgg(p, params.x, params.y);
  if (kind < 25.5) return sdParabolaSegment(p, params.x, params.y);
  if (kind < 26.5) return sdStairs(p, params.x, params.y, params.z);
  if (kind < 27.5) return sdCircleWave(p, params.x, params.y, params.z);
  if (kind < 28.5) return sdHyperbola(p, params.x);
  if (kind < 29.5) return sdCoolS(p, params.x, params.y);
  if (kind < 30.5) return sdPentagram(p, params.x);
  if (kind < 31.5) return sdRegularStar(p, params.x, params.y, params.z);
  if (kind < 32.5) return sdIsoscelesTriangle(p, params.x, params.y);
  if (kind < 33.5) return sdRoundedX(p, params.x, params.y, params.z);
  if (kind < 34.5) return sdCircleCross(p, params.x);
  if (kind < 36.5) return sdOrientedVesica(p, params.x, params.y, params.z);
  if (kind < 37.5) return sdParabola(p, params.x);
  if (kind < 38.5) return sdQuadraticCircle(p, params.x);
  if (kind < 39.5) return sdSquircle(p, params.x, params.y);
  if (kind < 40.5) return sdGear(p, params.x, params.y, params.z);
  if (kind < 41.5) return sdSpike(p, params.x, params.y);
  if (kind < 42.5) return sdPlus(p, params.x, params.y);
  if (kind < 43.5) return sdCloud(p, params.x);
  if (kind < 44.5) return sdLightning(p, params.x);
  if (kind < 45.5) return sdSpeechBubble(p, params.x, params.y, params.z);
  if (kind < 46.5) return sdHeartBroken(p, params.x);
  if (kind < 47.5) return sdDumbbell(p, params.x, params.y, params.z);
  if (kind < 48.5) return sdFlower(p, params.x, params.y);
  if (kind < 49.5) return sdShield(p, params.x, params.y);
  if (kind < 50.5) return sdBookmark(p, params.x, params.y);
  if (kind < 51.5) return sdArrow(p, params.x, params.y);
  if (kind < 52.5) return sdCog(p, params.x, params.y, params.z, params.w);
  if (kind < 53.5) return sdTapeStrip(p, params.x, params.y, params.z);
  if (kind < 54.5) return sdAsterisk(p, params.x, params.y);
  if (kind < 55.5) return sdSpiral(p, params.x, params.y, params.z, params.w);
  return sdPerCornerRect(p, b, radii);
}

in vec2 vLocal;
in vec2 vLocalScaled;
in vec2 vCorner;
in vec4 vFill;
in vec4 vStroke;
in vec4 vShadow;
in vec4 vShadowColor;
in float vOpacity;
in float vStrokeWidth;
in vec2 vVelClip;
flat in vec2 vHalfSize;
flat in vec4 vRadii;
flat in vec4 vFilterA;     // blur,brightness,contrast,saturate
flat in vec4 vFilterB;     // hueRotate,invert,_,_
flat in vec4 vGradient;
flat in float vSdfKind;
flat in vec4 vSdfParamsA;
flat in vec4 vSdfParamsB;
in vec2 vScreenUV;        // Layer 5: screen-space UV for backdrop-filter sample

layout(location = 0) out vec4 outColor;
layout(location = 1) out vec2 outVelocity;
uniform int uVelocityFormat;
uniform float uVelocityScale;

uniform sampler2D uFillTex;
uniform int uUseFillTex;

/** Layer 5: backdrop-filter uniforms. */
uniform sampler2D uBackdropTex;
uniform float uBackdropBlur;
uniform vec2 uBackdropTexel;

/**
 * Layer 6: mix-blend-mode uniforms.
 *  - uSceneTex:    sample of the scene at this fragment's UV (premultiplied).
 *  - uMixBlendMode: 0=normal, 1=multiply, 2=screen, 3=overlay, 4=darken, 5=lighten.
 *                   Int (not string) to avoid per-fragment string compares.
 *  - uMixBlendOpacity: extra alpha multiplier on the blended result (default 1).
 */
uniform sampler2D uSceneTex;
uniform int uMixBlendMode;
uniform float uMixBlendOpacity;

/**
 * Per-corner rounded-rect SDF.
 *
 * Maps a local point p (centered at rect center) to the inner half-size b
 * minus the corner radius for each corner, using a min/max selection that
 * is branchless in spirit (no if/else).
 *
 * r: vec4 of (TL, TR, BR, BL) radii.
 */
float sdPerCornerRect(vec2 p, vec2 b, vec4 r) {
  // For each corner, pick the radius depending on the sign of p:
  //   TL when (p.x<0 && p.y<0)
  //   TR when (p.x>0 && p.y<0)
  //   BR when (p.x>0 && p.y>0)
  //   BL when (p.x<0 && p.y>0)
  // Mix the four contributions by sign selectors.
  float rx =
      step(0.0, p.x) > 0.5 ? r.y : r.x; // TR or TL
  // (The above branchless mix would need explicit if/else in GLSL ES 3.0 —
  //  we use mix() with a step to select the right radius.)
  // Build a selector:
  //   For TL: p.x < 0 AND p.y < 0  → maskTL = (1-step(p.x,0)) * (1-step(p.y,0))
  //   For TR: p.x > 0 AND p.y < 0  → maskTR = step(p.x,0)    * (1-step(p.y,0))
  //   For BR: p.x > 0 AND p.y > 0  → maskBR = step(p.x,0)    * step(p.y,0)
  //   For BL: p.x < 0 AND p.y > 0  → maskBL = (1-step(p.x,0)) * step(p.y,0)
  float sx = step(0.0, p.x); // 1 if x >= 0, 0 if x < 0
  float sy = step(0.0, p.y); // 1 if y >= 0, 0 if y < 0
  float maskTL = (1.0 - sx) * (1.0 - sy);
  float maskTR = sx * (1.0 - sy);
  float maskBR = sx * sy;
  float maskBL = (1.0 - sx) * sy;
  float rad = maskTL * r.x + maskTR * r.y + maskBR * r.z + maskBL * r.w;

  vec2 q = abs(p) - b + vec2(rad);
  return length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0) - rad;
}

float shadowAlpha(float d, float blur) {
  float k = max(d, 0.0) / max(blur, 0.0001);
  float a = exp(-k * k * 2.0);
  return a;
}

/** HSL conversion for hue-rotate filter. */
vec3 rgb2hsl(vec3 c) {
  float maxC = max(max(c.r, c.g), c.b);
  float minC = min(min(c.r, c.g), c.b);
  float l = (maxC + minC) * 0.5;
  float h = 0.0, s = 0.0;
  if (maxC != minC) {
    float d = maxC - minC;
    s = l > 0.5 ? d / (2.0 - maxC - minC) : d / (maxC + minC);
    if (maxC == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
    else if (maxC == c.g) h = (c.b - c.r) / d + 2.0;
    else h = (c.r - c.g) / d + 4.0;
    h /= 6.0;
  }
  return vec3(h, s, l);
}

float hue2rgb(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0 / 6.0) return p + (q - p) * 6.0 * t;
  if (t < 1.0 / 2.0) return q;
  if (t < 2.0 / 3.0) return p + (q - p) * (2.0 / 3.0 - t) * 6.0;
  return p;
}

vec3 hsl2rgb(vec3 hsl) {
  float h = hsl.x, s = hsl.y, l = hsl.z;
  if (s == 0.0) return vec3(l);
  float q = l < 0.5 ? l * (1.0 + s) : l + s - l * s;
  float p = 2.0 * l - q;
  return vec3(
    hue2rgb(p, q, h + 1.0 / 3.0),
    hue2rgb(p, q, h),
    hue2rgb(p, q, h - 1.0 / 3.0)
  );
}

/**
 * CSS Compositing & Blending Level 1 — separable blend modes.
 * Operates on STRAIGHT (non-premultiplied) RGB. Caller is responsible for
 * straightening the inputs and re-premultiplying the output.
 *
 *  mode: 0=normal, 1=multiply, 2=screen, 3=overlay, 4=darken, 5=lighten,
 *        6=difference, 7=exclusion
 */
vec3 blendStraight(vec3 bg, vec3 src, int mode) {
  if (mode == 0) {
    return src;
  } else if (mode == 1) {
    // multiply: Cb * Cs
    return bg * src;
  } else if (mode == 2) {
    // screen: Cb + Cs - Cb*Cs
    return bg + src - bg * src;
  } else if (mode == 3) {
    // overlay: decide on backdrop brightness
    vec3 lo = 2.0 * bg * src;
    vec3 hi = 2.0 * bg + 2.0 * src - 2.0 * bg * src - 1.0;
    bvec3 dark = lessThanEqual(bg, vec3(0.5));
    return vec3(
      dark.x ? lo.x : hi.x,
      dark.y ? lo.y : hi.y,
      dark.z ? lo.z : hi.z
    );
  } else if (mode == 4) {
    // darken: min(Cb, Cs)
    return min(bg, src);
  } else if (mode == 5) {
    // lighten: max(Cb, Cs)
    return max(bg, src);
  } else if (mode == 6) {
    // difference: |Cb - Cs|
    return abs(bg - src);
  } else if (mode == 7) {
    // exclusion: Cb + Cs - 2*Cb*Cs
    return bg + src - 2.0 * bg * src;
  }
  return src;
}

/** Apply filter chain. Operates on premultiplied color; for simplicity we
 *  work in straight RGB (factor > 1 multiplies), then re-multiply by alpha. */
vec3 applyFilter(vec3 rgb, float a, float blurAmt, float brightness, float contrast, float saturate, float hueRotate, float invert) {
  // Blur: cheap soften — reduce contrast near edges (only meaningful for
  // soft edges; not visually equivalent to a real Gaussian but cheap).
  // For PoC, we just skip blur in this pass; the rect's SDF already provides
  // smooth edges, and a small uBlur in the FS effectively adds nothing for
  // our rounded rects.
  float _blurUnused = blurAmt;

  // Brightness: scale.
  rgb *= brightness;

  // Contrast: shift around 0.5.
  rgb = (rgb - 0.5) * contrast + 0.5;

  // Saturation: lerp to luma.
  if (saturate != 1.0) {
    float luma = dot(rgb, vec3(0.2126, 0.7152, 0.0722));
    rgb = mix(vec3(luma), rgb, saturate);
  }

  // Hue-rotate: HSL round-trip.
  if (hueRotate != 0.0) {
    vec3 hsl = rgb2hsl(rgb);
    hsl.x = fract(hsl.x + hueRotate / 6.2831853);
    rgb = hsl2rgb(hsl);
  }

  // Invert.
  if (invert > 0.5) {
    rgb = vec3(1.0) - rgb;
  }

  // Clamp.
  rgb = clamp(rgb, 0.0, 1.0);

  // Re-multiply by alpha so we stay in premultiplied form.
  return rgb * a;
}

void main() {
  // Pick the right SDF from the library via the per-instance kind id, then
  // fall through to the same AA-fill / stroke / shadow / blend pipeline as
  // before so every shape inherits the engine's full feature set.
  float d = sdDispatch(vLocal, vHalfSize, vSdfKind, vSdfParamsA, vSdfParamsB, vRadii);

  // Anti-alias using fwidth, clamped to a sane minimum. Without the clamp,
  // fwidth can exceed the rect pixel size at low renderScale and the
  // smoothstep clamps to binary values that produce visible banding rather
  // than a clean edge AA.
  float aa = max(fwidth(d), 0.5);
  float fillMask = 1.0 - smoothstep(-aa, aa, d);

  // Stroke band — inside the rect, near the edge.
  float halfStroke = max(vStrokeWidth, 0.0) * 0.5;
  float strokeMask = 1.0 - smoothstep(-aa, aa, abs(d) - halfStroke);
  float inside = step(d, 0.0);
  float strokeCoverage = strokeMask * inside * vStroke.w;

  // Shadow.
  vec2 shadowB = vHalfSize + vec2(vShadow.w);
  vec2 shadowLocal = vLocal - vec2(vShadow.x, vShadow.y);
  // Shadow uses the maximum corner radius for simplicity (cheap).
  float shadowR = max(max(vRadii.x, vRadii.y), max(vRadii.z, vRadii.w));
  float dShadow = sdPerCornerRect(shadowLocal, shadowB, vec4(shadowR));
  float shAlpha = shadowAlpha(dShadow, max(vShadow.z, 0.5));

  // Composite.
  vec3 shadowRgb = vShadowColor.rgb * vShadowColor.a * shAlpha * vOpacity;
  float aShadow = vShadowColor.a * shAlpha * vOpacity;

  vec4 texSample = texture(uFillTex, vCorner);
  // Gradient: if vGradient.w != 0, mix from vFill.rgb → vGradient.rgb along
  // the gradient axis. vLocal is centered on the rect; project onto the angle
  // direction and normalize by half-extents so the gradient spans 0..1 across
  // the rect.
  vec3 solidRgb = vFill.rgb;
  float gradT = 0.0;
  if (abs(vGradient.w) > 0.001) {
    float ca = cos(vGradient.w);
    float sa = sin(vGradient.w);
    // project local into gradient direction (in CSS px; rect spans -half..+half)
    float along = (vLocal.x * ca + vLocal.y * sa) / max(max(vHalfSize.x, vHalfSize.y), 1.0);
    gradT = clamp(along * 0.5 + 0.5, 0.0, 1.0);
    solidRgb = mix(vFill.rgb, vGradient.rgb, gradT);
  }
  float solidA = vFill.a;
  vec3 texRgb = texSample.rgb;
  float texA = texSample.a;
  vec3 mixRgb = mix(solidRgb, texRgb, float(uUseFillTex));
  float mixA = mix(solidA, texA, float(uUseFillTex));

  vec3 fillRgb = mixRgb * mixA * fillMask * vOpacity;
  float aFill = mixA * fillMask * vOpacity;

  vec3 strokeRgb = vStroke.rgb * strokeCoverage * vOpacity;
  float aStroke = strokeCoverage * vOpacity;

  // Apply filter chain to the composited fill+stroke color before blending
  // with shadow (so shadow is unaffected — keeps cheap shadows cheap).
  // Filter values are per-instance via vFilterA/vFilterB (see compiler.ts).
  vec3 fsRgb = fillRgb + strokeRgb * (1.0 - aFill);
  float fsA = aFill + aStroke * (1.0 - aFill);
  // Filter operates on straight color; divide by alpha to get straight RGB,
  // apply, then re-multiply. If alpha is 0, we skip (no contribution anyway).
  if (fsA > 0.001) {
    vec3 straight = fsRgb / fsA;
    straight = applyFilter(
      straight,
      1.0,
      vFilterA.x,                // blur (CSS px; PoC no-op)
      vFilterA.y,                // brightness
      vFilterA.z,                // contrast
      vFilterA.w,                // saturate
      vFilterB.x,                // hueRotate (radians)
      vFilterB.y                 // invert (0/1)
    );
    fsRgb = straight * fsA;
  }

  // Layer 5: backdrop-filter blur. Sample scene FBO at this fragment's screen
  // UV and apply a cheap 9-tap blur. The blurred scene is composited BENEATH
  // shadow + node body (CSS spec semantics). Shadow is unaffected because it
  // extends outside the rect; fillMask confines the backdrop to the rect's
  // rounded shape so it doesn't bleed past rounded corners.
  //
  // Final stack (bottom → top): backdrop (clipped to rect) → shadow → fill+stroke.
  vec3 cFinal;
  float aFinal;
  if (uBackdropBlur > 0.0) {
    // CSS px → texel offset. uBackdropTexel = 1/width,1/height in UV units.
    // The CSS blur radius must be converted to screen pixels first; we
    // approximate by treating CSS px as canvas px (the engine is sized 1:1).
    vec2 ofs = uBackdropTexel * uBackdropBlur;
    vec3 acc = vec3(0.0);
    acc += texture(uBackdropTex, vScreenUV).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2( ofs.x, 0.0)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2(-ofs.x, 0.0)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2(0.0,  ofs.y)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2(0.0, -ofs.y)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2( ofs.x,  ofs.y)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2(-ofs.x,  ofs.y)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2( ofs.x, -ofs.y)).rgb;
    acc += texture(uBackdropTex, vScreenUV + vec2(-ofs.x, -ofs.y)).rgb;
    vec3 blurRgb = acc * (1.0 / 9.0);
    float aBlur = fillMask * vOpacity;
    vec3 bdRgb = blurRgb * aBlur;
    // backdrop → shadow → fs (over operators).
    vec3 cShadowOverBd = shadowRgb + bdRgb * (1.0 - aShadow);
    float aShadowOverBd = aShadow + aBlur * (1.0 - aShadow);
    cFinal = fsRgb + cShadowOverBd * (1.0 - fsA);
    aFinal = fsA + aShadowOverBd * (1.0 - fsA);
  } else {
    // Existing path: shadow under node body.
    cFinal = shadowRgb + fsRgb * (1.0 - aShadow);
    aFinal = aShadow + fsA * (1.0 - aShadow);
  }

  // Layer 6: mix-blend-mode. When not "normal", sample the scene at this
  // fragment's UV, straighten both src and bg, blend per the CSS spec, then
  // re-premultiply by alpha. The two-pass setup (copy sceneFbo → scratch,
  // draw blend rects into scratch using scratch.tex as the input, then blit
  // scratch → sceneFbo) means outside this rect's pixels, the FS writes
  // effectively transparent (aFinal ≈ 0) and the scratch stays as the copied
  // scene — no per-rect scissor needed.
  if (uMixBlendMode != 0) {
    vec4 bg = texture(uSceneTex, vScreenUV);
    vec3 srcStraight = aFinal > 0.001 ? cFinal / aFinal : vec3(0.0);
    vec3 bgStraight = bg.a > 0.001 ? bg.rgb / bg.a : vec3(0.0);
    vec3 blended = blendStraight(bgStraight, srcStraight, uMixBlendMode);
    float aBlend = clamp(aFinal * uMixBlendOpacity, 0.0, 1.0);
    cFinal = blended * aBlend;
    aFinal = aBlend;
  }

  outColor = vec4(cFinal, aFinal);

  // Velocity output.
  float visibleMask = max(aFinal, shAlpha * vOpacity);
  vec2 vel = vVelClip * uVelocityScale * visibleMask;
  if (uVelocityFormat == 2) {
    outVelocity = vel;
  } else if (uVelocityFormat == 1) {
    outVelocity = vel * 127.0;
  } else {
    outVelocity = vec2(0.0);
  }
}
`;

export interface RectPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  instanceBuffer: WebGLBuffer;
  quadBuffer: WebGLBuffer;
  uCanvasSize: WebGLUniformLocation | null;
  uDpr: WebGLUniformLocation | null;
  uVelocityFormat: WebGLUniformLocation | null;
  uVelocityScale: WebGLUniformLocation | null;
  uFillTex: WebGLUniformLocation | null;
  uUseFillTex: WebGLUniformLocation | null;
  // Layer 5: backdrop-filter (still global — not per-node for PoC; could be
  // moved into attr 11/12 if per-node backdrop blur is later required).
  uBackdropTex: WebGLUniformLocation | null;
  uBackdropBlur: WebGLUniformLocation | null;
  uBackdropTexel: WebGLUniformLocation | null;
  // Layer 6: mix-blend-mode (global — one blend mode per draw call since
  // partitioner groups consecutive same-mode nodes).
  uSceneTex: WebGLUniformLocation | null;
  uMixBlendMode: WebGLUniformLocation | null;
  uMixBlendOpacity: WebGLUniformLocation | null;
  capacity: number;
}

const QUAD_VERTS = new Float32Array([
  0, 0,
  1, 0,
  0, 1,
  0, 1,
  1, 0,
  1, 1,
]);

export function createRectPipeline(gl: WebGL2RenderingContext): RectPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, RECT_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const instanceBuffer = gl.createBuffer();
  const quadBuffer = gl.createBuffer();
  if (!vao || !instanceBuffer || !quadBuffer) return null;

  gl.bindVertexArray(vao);

  // Per-vertex attributes: none. The unit-quad corner is derived from
  // gl_VertexID inside the vertex shader (see VERT_SRC comment). This
  // frees all 16 vertex attribute locations for per-instance data.

  // Instance buffer: 15 vec4 attributes — filterA/B/gradient at 10/11/12,
  // SDF library at 13/14, baseline rect attrs at 0-9. Total stride is
  // 240 bytes (under WebGL2's MAX_VERTEX_ATTRIB_STRIDE = 255).
  gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
  const stride = FLOATS_PER_INSTANCE * 4;
  const offsets: [number, number, number][] = [
    [ATTR_LOCATIONS.posSize,    4,   0],
    [ATTR_LOCATIONS.fill,       4,  16],
    [ATTR_LOCATIONS.stroke,     4,  32],
    [ATTR_LOCATIONS.shadow,     4,  48],
    [ATTR_LOCATIONS.shadowColor,4,  64],
    [ATTR_LOCATIONS.xform,      4,  80],
    [ATTR_LOCATIONS.prev,       4,  96],
    [ATTR_LOCATIONS.misc,       4, 112],
    [ATTR_LOCATIONS.halfSize,   4, 128],
    [ATTR_LOCATIONS.radii,      4, 144],
    [ATTR_LOCATIONS.filterA,    4, 160],
    [ATTR_LOCATIONS.filterB,    4, 176],
    [ATTR_LOCATIONS.gradient,   4, 192],
    [ATTR_LOCATIONS.sdfParamsA, 4, 208],
    [ATTR_LOCATIONS.sdfParamsB, 4, 224],
  ];
  for (const [loc, comps, off] of offsets) {
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, comps, gl.FLOAT, false, stride, off);
    gl.vertexAttribDivisor(loc, 1);
  }

  gl.bindVertexArray(null);

  const uCanvasSize = gl.getUniformLocation(program, "uCanvasSize");
  const uDpr = gl.getUniformLocation(program, "uDevicePixelRatio");
  const uVelocityFormat = gl.getUniformLocation(program, "uVelocityFormat");
  const uVelocityScale = gl.getUniformLocation(program, "uVelocityScale");
  const uFillTex = gl.getUniformLocation(program, "uFillTex");
  const uUseFillTex = gl.getUniformLocation(program, "uUseFillTex");
  const uBackdropTex = gl.getUniformLocation(program, "uBackdropTex");
  const uBackdropBlur = gl.getUniformLocation(program, "uBackdropBlur");
  const uBackdropTexel = gl.getUniformLocation(program, "uBackdropTexel");
  const uSceneTex = gl.getUniformLocation(program, "uSceneTex");
  const uMixBlendMode = gl.getUniformLocation(program, "uMixBlendMode");
  const uMixBlendOpacity = gl.getUniformLocation(program, "uMixBlendOpacity");

  return {
    program,
    vao,
    instanceBuffer,
    quadBuffer,
    uCanvasSize,
    uDpr,
    uVelocityFormat,
    uVelocityScale,
    uFillTex,
    uUseFillTex,
    uBackdropTex, uBackdropBlur, uBackdropTexel,
    uSceneTex, uMixBlendMode, uMixBlendOpacity,
    capacity: 0,
  };
}

export function uploadInstances(
  gl: WebGL2RenderingContext,
  pipe: RectPipeline,
  compiled: CompiledScene,
): void {
  if (compiled.count === 0) return;
  gl.bindBuffer(gl.ARRAY_BUFFER, pipe.instanceBuffer);
  if (compiled.count > pipe.capacity) {
    const newCap = Math.max(pipe.capacity * 2, compiled.count + 64);
    gl.bufferData(gl.ARRAY_BUFFER, newCap * BYTES_PER_INSTANCE, gl.DYNAMIC_DRAW);
    pipe.capacity = newCap;
  }
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, compiled.data.subarray(0, compiled.count * FLOATS_PER_INSTANCE));
}

/** Filter state stored on each SceneNode; baked into the instance buffer.
 *  Layer 7: blur/brightness/contrast/saturate/hueRotate/invert are now
 *  per-instance via attr 10/11 (vFilterA/vFilterB varyings) — see compiler.ts.
 *  `backdropBlur` remains a global uniform (Layer 5). */
export interface FilterState {
  blur: number;
  brightness: number;
  contrast: number;
  saturate: number;
  hueRotate: number;
  invert: number;
  /** Backdrop blur radius in CSS px (0 disables). */
  backdropBlur: number;
}

export const IDENTITY_FILTER: FilterState = {
  blur: 0,
  brightness: 1,
  contrast: 1,
  saturate: 1,
  hueRotate: 0,
  invert: 0,
  backdropBlur: 0,
};

export interface DrawBackdrop {
  /** Scene FBO texture to sample. Null disables backdrop-filter. */
  tex: WebGLTexture | null;
  /** Scene FBO dimensions in pixels (for texel-size uniform). */
  width: number;
  height: number;
  /** Backdrop blur radius in CSS px (0 disables). */
  blur: number;
}

export const NO_BACKDROP: DrawBackdrop = {
  tex: null,
  width: 0,
  height: 0,
  blur: 0,
};

/**
 * Layer 6: mix-blend-mode draw params. One per draw call — the partitioner
 * in main.ts groups consecutive same-mode nodes into a single draw call so a
 * uniform (not an attribute) is the right surface for this state.
 *
 *  - tex:     Scene texture to sample for the "behind" color. Pass the scratch
 *             FBO's texture when blendMode != normal (per-segment copy at the
 *             start of the blend segment puts the unmodified scene into it).
 *  - mode:    0=normal, 1=multiply, 2=screen, 3=overlay, 4=darken, 5=lighten,
 *             6=difference, 7=exclusion. Int matches the GLSL uniform encoding.
 *  - opacity: extra alpha multiplier on the blended result (CSS-style separate
 *             from the rect's own opacity). 0..1, default 1.
 */
export interface DrawMixBlend {
  /** Scene FBO texture to sample. Null disables mix-blend-mode (uses normal blend). */
  tex: WebGLTexture | null;
  /** Blend mode int (0=normal, 1=multiply, 2=screen, 3=overlay, 4=darken,
   *  5=lighten, 6=difference, 7=exclusion). */
  mode: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** CSS mix-blend-opacity multiplier on the rect's alpha. */
  opacity: number;
}

export const NO_MIX_BLEND: DrawMixBlend = {
  tex: null,
  mode: 0,
  opacity: 1,
};

export function drawRects(
  gl: WebGL2RenderingContext,
  pipe: RectPipeline,
  compiled: CompiledScene,
  canvasSizeCss: [number, number],
  dpr: number,
  velocityFormat: 0 | 1 | 2 = 0,
  velocityScale: number = 1.0,
  fillTex: WebGLTexture | null = null,
  useFillTex: 0 | 1 = 0,
  backdrop: DrawBackdrop = NO_BACKDROP,
  mixBlend: DrawMixBlend = NO_MIX_BLEND,
): void {
  if (compiled.count === 0) return;
  gl.useProgram(pipe.program);
  gl.bindVertexArray(pipe.vao);
  uploadInstances(gl, pipe, compiled);
  gl.uniform2f(pipe.uCanvasSize, canvasSizeCss[0], canvasSizeCss[1]);
  gl.uniform1f(pipe.uDpr, dpr);
  if (pipe.uVelocityFormat) gl.uniform1i(pipe.uVelocityFormat, velocityFormat);
  if (pipe.uVelocityScale) gl.uniform1f(pipe.uVelocityScale, velocityScale);

  gl.activeTexture(gl.TEXTURE2);
  if (fillTex) {
    gl.bindTexture(gl.TEXTURE_2D, fillTex);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, WHITE_TEX);
  }
  if (pipe.uFillTex) gl.uniform1i(pipe.uFillTex, 2);
  if (pipe.uUseFillTex) gl.uniform1i(pipe.uUseFillTex, useFillTex);

  // Guard against a Framebuffer↔Texture feedback loop: any sampler that
  // isn't actively bound this draw could still hold a reference to a
  // texture attached to the currently-bound FBO (e.g. sceneFbo.tex from a
  // prior postprocess pass). Bind WHITE_TEX to units 3 and 4 by default so
  // they're safe regardless of caller state; if the caller actually uses
  // them (backdrop-filter on unit 3, mix-blend-mode on unit 4) we rebind
  // below. This is the only way the fragment shader can sample from units 3
  // and 4 without risking a feedback loop into sceneFbo.fbo.
  gl.activeTexture(gl.TEXTURE3);
  gl.bindTexture(gl.TEXTURE_2D, WHITE_TEX);
  gl.activeTexture(gl.TEXTURE4);
  gl.bindTexture(gl.TEXTURE_2D, WHITE_TEX);

  // Layer 5: bind scene texture to unit 3 only when this segment uses
  // backdrop-filter. The shader gates the sample on uBackdropBlur > 0.
  if (pipe.uBackdropTex) gl.uniform1i(pipe.uBackdropTex, 3);
  if (pipe.uBackdropBlur) gl.uniform1f(pipe.uBackdropBlur, backdrop.blur);
  if (pipe.uBackdropTexel && backdrop.width > 0 && backdrop.height > 0) {
    gl.uniform2f(pipe.uBackdropTexel, 1 / backdrop.width, 1 / backdrop.height);
  }
  if (backdrop.blur > 0 && backdrop.tex) {
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, backdrop.tex);
  }

  // Layer 6: mix-blend-mode uniforms. Unit 4 holds the scene texture for
  // the "behind" color when blendMode != 0. We already bound WHITE_TEX as
  // a safe fallback; rebind the real texture when needed.
  if (pipe.uSceneTex) gl.uniform1i(pipe.uSceneTex, 4);
  if (pipe.uMixBlendMode) gl.uniform1i(pipe.uMixBlendMode, mixBlend.mode);
  if (pipe.uMixBlendOpacity) gl.uniform1f(pipe.uMixBlendOpacity, mixBlend.opacity);
  if (mixBlend.mode !== 0 && mixBlend.tex) {
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, mixBlend.tex);
  }

  gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, compiled.count);
  gl.bindVertexArray(null);
}

/** 1×1 white texture used as the default sampler when a draw has no fill tex. */
let WHITE_TEX: WebGLTexture | null = null;
export function ensureWhiteTex(gl: WebGL2RenderingContext): WebGLTexture {
  if (WHITE_TEX) return WHITE_TEX;
  const t = gl.createTexture();
  if (!t) throw new Error("could not create white tex");
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([255, 255, 255, 255]));
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  WHITE_TEX = t;
  return t;
}